import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { getCounterState } from '@api/counter';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { useAuth } from '@/contexts/auth-context';
import { AnimatedAmount } from '../components/counter/animated-amount';
import { CounterAmbient } from '../components/counter/counter-ambient';
import { CounterBurst } from '../components/counter/counter-burst';

export const Route = createFileRoute('/counter')({
  component: CounterScreen,
});

const POLL_MS = 4000;

function CounterScreen() {
  const { isAuthenticated } = useAuth();
  const [signInError, setSignInError] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['counter'],
    queryFn: getCounterState,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Presenting = Fullscreen API OR native (F11) fullscreen. F11 can't be
  // observed directly, so infer it when the viewport fills the whole screen.
  const [fsApi, setFsApi] = useState(false);
  const [nativeFs, setNativeFs] = useState(false);
  useEffect(() => {
    const onFsChange = () => setFsApi(Boolean(document.fullscreenElement));
    const onResize = () =>
      setNativeFs(window.innerHeight >= screen.height - 3);
    document.addEventListener('fullscreenchange', onFsChange);
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  const presenting = fsApi || nativeFs;

  const enterPresent = () => {
    containerRef.current?.requestFullscreen?.().catch(() => {});
  };

  // While presenting (incl. native F11, where the whole page is fullscreen),
  // lock body scroll so the screen never scrolls.
  useEffect(() => {
    if (!presenting) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [presenting]);

  // Fire a burst + pop whenever the amount ticks up.
  const [burstKey, setBurstKey] = useState(0);
  const prevAmount = useRef<number | null>(null);
  useEffect(() => {
    if (data?.enabled) {
      if (prevAmount.current !== null && data.amountCents > prevAmount.current) {
        setBurstKey((k) => k + 1);
      }
      prevAmount.current = data.amountCents;
    }
  }, [data?.enabled, data?.amountCents]);

  if (!isLoading && (!data || !data.enabled)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        Nothing to see here yet.
      </div>
    );
  }

  // Big and bold regardless of how fullscreen was entered; even larger when
  // we detect a present/fullscreen state.
  const amountFontSize = presenting
    ? 'clamp(5rem, 20vw, 28rem)'
    : 'clamp(4rem, 16vw, 22rem)';

  return (
    <div
      ref={containerRef}
      className={`flex flex-col items-center justify-center overflow-hidden bg-background text-foreground ${
        // When presenting, cover the sticky site header (z-40) and fill the
        // viewport exactly so nothing scrolls.
        presenting
          ? 'fixed inset-0 z-50 h-screen w-screen'
          : // Fill the viewport minus the h-14 (3.5rem) sticky header so the
            // windowed page never scrolls.
            'relative min-h-[calc(100dvh-3.5rem)]'
      }`}
    >
      <style>{
        '@keyframes counter-shimmer { from { background-position: 250% 0; } to { background-position: -250% 0; } }'
      }</style>
      <CounterAmbient intense={presenting} />

      <div className="relative z-10 flex flex-col items-center gap-6 px-6 text-center">
        {/* Pulsing realtime indicator. */}
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <motion.span
              className="absolute inline-flex h-full w-full rounded-full bg-primary"
              animate={{ scale: [1, 2.4], opacity: [0.6, 0] }}
              transition={{
                duration: 1.8,
                repeat: Number.POSITIVE_INFINITY,
                ease: 'easeOut',
              }}
            />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
          </span>
          <span className="text-xs font-medium uppercase tracking-[0.3em] text-primary/80">
            Live
          </span>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className={`font-semibold uppercase text-neutral-300 ${
            presenting
              ? 'text-3xl tracking-[0.35em] sm:text-5xl'
              : 'text-xl tracking-[0.3em] sm:text-3xl'
          }`}
        >
          {data?.title}
        </motion.p>

        <div className="relative flex items-center justify-center">
          <CounterBurst triggerKey={burstKey} />

          {/* Glow that flares on each tick. */}
          <motion.div
            key={`glow-${burstKey}`}
            aria-hidden
            className="pointer-events-none absolute -inset-x-24 -inset-y-12 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(circle, rgba(99,218,185,0.55), rgba(205,245,100,0.22) 45%, transparent 70%)',
            }}
            initial={{ opacity: 0.45, scale: 1 }}
            animate={
              burstKey > 0
                ? { opacity: [0.9, 0.5], scale: [1.15, 1] }
                : { opacity: [0.4, 0.6, 0.4] }
            }
            transition={
              burstKey > 0
                ? { duration: 0.8, ease: 'easeOut' }
                : { duration: 5, repeat: Number.POSITIVE_INFINITY }
            }
          />

          {/* Gentle continuous breathing keeps it alive between ticks. */}
          <motion.div
            animate={{ scale: [1, 1.015, 1] }}
            transition={{
              duration: 5,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
            }}
          >
            <motion.div
              key={`pop-${burstKey}`}
              initial={burstKey > 0 ? { scale: 1 } : false}
              animate={burstKey > 0 ? { scale: [1, 1.12, 1] } : {}}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              style={{ fontSize: amountFontSize }}
              className="relative font-black leading-none tabular-nums tracking-tight"
            >
              {/* A teal sheen sweeps across the digits on a slow loop (CSS
                  keyframes — framer-motion doesn't animate background-position
                  reliably). */}
              <span
                className="bg-clip-text text-transparent drop-shadow-[0_0_45px_rgba(99,218,185,0.45)]"
                style={{
                  backgroundImage:
                    'linear-gradient(110deg, #ffffff 0%, #ffffff 38%, #63dab9 50%, #ffffff 62%, #ffffff 100%)',
                  backgroundSize: '250% 100%',
                  animation: 'counter-shimmer 7s linear infinite',
                }}
              >
                {data && (
                  <AnimatedAmount
                    valueCents={data.amountCents}
                    currency={data.currency}
                  />
                )}
              </span>
            </motion.div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className={`text-neutral-400 ${
            presenting ? 'text-xl' : 'text-sm sm:text-base'
          }`}
        >
          from{' '}
          <span className="font-semibold text-primary">
            {data?.signups.toLocaleString()}
          </span>{' '}
          {data?.signups === 1 ? 'signup' : 'signups'}
        </motion.p>
      </div>

      {/* Logged-out visitors (e.g. arriving from a campaign link) get a
          sign-in prompt. Hidden while presenting on a display. Signing in
          keeps them here (stayOnPage) — the prompt then disappears. */}
      {!presenting && !isAuthenticated && (
        <div className="absolute inset-x-0 top-5 z-20 flex justify-center px-4">
          <div className="flex w-full max-w-[17rem] flex-col items-center gap-2.5 rounded-2xl border border-neutral-700/70 bg-neutral-900/60 px-4 py-3 text-center shadow-lg shadow-black/30 backdrop-blur-md">
            <p className="text-[0.7rem] font-medium uppercase tracking-[0.25em] text-neutral-400">
              Sign in to join
            </p>
            <GoogleSignInButton
              stayOnPage
              onError={setSignInError}
              className="w-full"
            />
            {signInError && (
              <p role="alert" className="text-xs text-destructive">
                {signInError}
              </p>
            )}
          </div>
        </div>
      )}

      {!presenting && (
        <button
          type="button"
          onClick={enterPresent}
          className="absolute bottom-6 right-6 z-10 rounded-full border border-neutral-700 bg-neutral-900/70 px-4 py-2 text-sm text-neutral-200 backdrop-blur transition-colors hover:bg-neutral-800"
        >
          Present ⛶
        </button>
      )}
    </div>
  );
}
